import * as THREE from 'three';

/**
 * Locomotion comfort vignette.
 *
 * The single largest cause of simulator sickness is optical flow in the
 * periphery that the inner ear did not agree to: the player is standing still
 * and the world is moving. Narrowing the field of view during artificial
 * locomotion removes most of that peripheral flow and is the one comfort
 * measure with consistent evidence behind it.
 *
 * Implemented as a camera-parented ring rather than a post pass because it has
 * to be in the eye's frame, not the framebuffer's: it must sit at a fixed
 * angular radius around each eye's optical axis, and a screen-space vignette
 * applied to a stereo framebuffer is neither (it would be centred on the
 * viewport, which is not where the eye is looking through).
 *
 * Drawn depth-test-free at 0.35 m with a large renderOrder, so it is the last
 * thing composited into the forward pass and nothing can poke through it.
 */
export class ComfortVignette {
  constructor() {
    this.uniforms = {
      // x: inner radius (fully clear), y: outer radius (fully black)
      uRadius: { value: new THREE.Vector2(0.62, 1.0) },
      uOpacity: { value: 0 },
    };

    const material = new THREE.ShaderMaterial({
      name: 'xr-comfort',
      uniforms: this.uniforms,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
        }
      `,
      fragmentShader: /* glsl */ `
        varying vec2 vUv;
        uniform vec2 uRadius;
        uniform float uOpacity;
        void main() {
          float r = length( vUv * 2.0 - 1.0 );
          float a = smoothstep( uRadius.x, uRadius.y, r ) * uOpacity;
          if ( a <= 0.002 ) discard;
          gl_FragColor = vec4( 0.0, 0.0, 0.0, a );
        }
      `,
    });
    // The material patcher only touches lit materials, but say so explicitly:
    // this quad must never acquire the shadow/AO injection.
    material.userData.owNoPatch = true;

    // Generous enough to cover the widest headset FOV at this distance.
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(2.2, 2.2), material);
    this.mesh.position.set(0, 0, -0.35);
    this.mesh.renderOrder = 10000;
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
    this.mesh.name = 'xr-comfort-vignette';
    // Keep it out of the prepass and the cascades: it is a screen effect that
    // happens to be geometry, and a 2.2 m black plane in the depth buffer would
    // bury the volumetrics and cast a shadow across the street.
    this.mesh.userData.owNoPrepass = true;
    this.mesh.userData.owNoShadow = true;

    this._level = 0;
  }

  /**
   * @param {number} dt
   * @param {number} target 0..1 — how much tunnelling this frame wants
   */
  update(dt, target) {
    // Closing fast and opening slow: the narrowing has to lead the motion to be
    // any use, but a fast re-open is itself a flow event.
    const tau = target > this._level ? 0.06 : 0.22;
    const k = 1 - Math.exp(-dt / tau);
    this._level += (target - this._level) * k;
    if (this._level < 1e-3) this._level = 0;
    this.uniforms.uOpacity.value = this._level;
    // Tighten the aperture as well as darkening it, so a full-strength tunnel
    // is a genuine FOV restriction rather than a grey wash.
    this.uniforms.uRadius.value.set(0.62 - 0.34 * this._level, 1.0 - 0.28 * this._level);
    this.mesh.visible = this._level > 0;
  }

  dispose() {
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
    this.mesh.parent?.remove(this.mesh);
  }
}
